import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { jobsAPI } from '../services/api';

interface Job {
  id: string;
  short_id: string | null;
  job_name: string;
  job_type: string;
  status: string;
  progress_percent: number;
  estimated_duration: number | null;
  started_at: string | null;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  notice_filename: string | null;
  proposal_filenames: string[] | null;
}

/**
 * Parse UTC time string to timestamp.
 */
const parseUtcTime = (utcString: string): number => {
  const iso = utcString.replace(' ', 'T');
  const withTz = iso.includes('+') || iso.includes('Z') ? iso : iso + 'Z';
  return new Date(withTz).getTime();
};

/**
 * Format UTC time string for display in Moscow timezone (UTC+3).
 */
const formatMoscowTime = (utcString: string): string => {
  const timestamp = parseUtcTime(utcString);
  return new Date(timestamp).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });
};

/**
 * Calculate progress based on elapsed time and estimated duration.
 */
const calculateProgress = (job: Job): number => {
  if (job.status === 'completed') return 100;
  if (job.status === 'error') return job.progress_percent;
  if (job.status !== 'processing') return 0;

  if (!job.estimated_duration || !job.started_at) {
    return Math.max(job.progress_percent || 5, 5);
  }

  const startTimeUtc = parseUtcTime(job.started_at);
  const nowUtc = Date.now();
  const elapsedSeconds = (nowUtc - startTimeUtc) / 1000;
  const rawProgress = (elapsedSeconds / job.estimated_duration) * 100;
  const steppedProgress = Math.floor(rawProgress / 5) * 5;

  return Math.max(5, Math.min(steppedProgress, 95));
};

/**
 * Format job display name using Moscow time from created_at instead of UTC time from job_name.
 */
const formatJobDisplayName = (job: Job): string => {
  const jobTypeNames: Record<string, string> = {
    'analyze_price': 'Анализ цен',
    'analyze_property': 'Анализ характеристик'
  };

  const typeName = jobTypeNames[job.job_type] || job.job_type;
  const moscowTime = formatMoscowTime(job.created_at);

  return `${typeName} ${moscowTime}`;
};

/**
 * Format duration in human readable format.
 */
const formatDuration = (startTime: string, endTime: string): string => {
  const start = parseUtcTime(startTime);
  const end = parseUtcTime(endTime);
  const durationMs = end - start;

  if (durationMs < 0) return '';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours} ч ${remainingMinutes} мин`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes} мин ${remainingSeconds} сек`;
  } else {
    return `${seconds} сек`;
  }
};

/**
 * Format remaining time.
 */
const formatRemainingTime = (job: Job): string | null => {
  if (job.status !== 'processing') return null;

  if (job.estimated_duration && !job.started_at) {
    const minutes = Math.floor(job.estimated_duration / 60);
    return `Оценка: ~${minutes} мин`;
  }

  if (!job.estimated_duration || !job.started_at) return null;

  const startTimeUtc = parseUtcTime(job.started_at);
  const nowUtc = Date.now();
  const elapsedSeconds = (nowUtc - startTimeUtc) / 1000;
  const remainingSeconds = Math.max(0, job.estimated_duration - elapsedSeconds);

  if (remainingSeconds <= 0) return 'Оценка времени вышла, задача ещё выполняется';

  const minutes = Math.floor(remainingSeconds / 60);
  return minutes > 0
    ? `Осталось: ~${minutes} мин`
    : 'Осталось меньше минуты...';
};

const JOB_TYPE_NAMES: Record<string, string> = {
  '': 'Все типы',
  'analyze_price': 'Анализ цен',
  'analyze_property': 'Анализ характеристик'
};

const ITEMS_PER_PAGE = 6;

const JobsPage: React.FC = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [expandedFilesJobId, setExpandedFilesJobId] = useState<string | null>(null);

  const totalPages = Math.ceil(totalJobs / ITEMS_PER_PAGE);

  // Get file labels based on job type
  const getFileLabels = (jobType: string) => {
    if (jobType === 'analyze_price') {
      return { notice: 'Потребность', proposals: 'КП от поставщиков' };
    }
    return { notice: 'Извещение о закупке', proposals: 'Заявки от участников' };
  };

  const loadJobs = useCallback(async () => {
    try {
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const data = await jobsAPI.listByType(
        jobTypeFilter || undefined as any,
        statusFilter || undefined,
        ITEMS_PER_PAGE,
        offset,
        searchQuery || undefined
      );
      setJobs(data.jobs);
      setTotalJobs(data.total);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [jobTypeFilter, statusFilter, searchQuery, currentPage]);

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [loadJobs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [jobTypeFilter, statusFilter, searchQuery]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const downloadResult = async (jobId: string, jobName: string) => {
    try {
      const blob = await jobsAPI.download(jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${jobName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download result:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const confirmDeleteJob = async (jobId: string) => {
    try {
      await jobsAPI.delete(jobId);
      setDeletingJobId(null);
      // Reload jobs after deletion
      loadJobs();
    } catch (error) {
      console.error('Failed to delete job:', error);
      setDeletingJobId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'processing': return '#2196f3';
      case 'error': return '#f44336';
      default: return '#ff9800';
    }
  };

  const getJobTypeColor = (jobType: string) => {
    switch (jobType) {
      case 'analyze_price': return '#b05e27ff';
      case 'analyze_property': return '#009688';
      default: return '#607d8b';
    }
  };

  if (loading && jobs.length === 0) {
    return <div>Загрузка...</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>{t('jobs.title')}</h2>

      {/* Filters Panel */}
      <div style={{
        // backgroundColor: 'white',
        padding: '20px',
        borderRadius: '0px',
        // boxShadow: '0 2px 8px rgba(14, 14, 14, 0.04)',
        marginBottom: '20px'
      }}>
        {/* Row 1: Job Type + Search */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '15px', flexWrap: 'wrap', gap: '15px' }}>
          {/* Job Type Filter */}
          <div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Тип задачи:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['', 'analyze_price', 'analyze_property'].map((type) => (
                <button
                  key={type}
                  onClick={() => setJobTypeFilter(type)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: jobTypeFilter === type ? '#1976d2' : '#f5f5f5',
                    color: jobTypeFilter === type ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {JOB_TYPE_NAMES[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Search by short_id */}
          <div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Поиск по id:</div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="#123456"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  width: '140px',
                  fontFamily: 'monospace'
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    marginLeft: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Очистить
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Status Filter */}
        <div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Статус:</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusFilter('')}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === '' ? '#1976d2' : '#f5f5f5',
                color: statusFilter === '' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('jobs.filterAll')}
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === 'pending' ? '#1976d2' : '#f5f5f5',
                color: statusFilter === 'pending' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('jobs.filterPending')}
            </button>
            <button
              onClick={() => setStatusFilter('processing')}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === 'processing' ? '#1976d2' : '#f5f5f5',
                color: statusFilter === 'processing' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('jobs.filterProcessing')}
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === 'completed' ? '#1976d2' : '#f5f5f5',
                color: statusFilter === 'completed' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('jobs.filterCompleted')}
            </button>
            <button
              onClick={() => setStatusFilter('error')}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === 'error' ? '#1976d2' : '#f5f5f5',
                color: statusFilter === 'error' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('jobs.filterError')}
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          {t('jobs.noJobs')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {jobs.map((job) => (
            <div
              key={job.id}
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.49)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                    {job.short_id && (
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        backgroundColor: '#e3f2fd',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        #{job.short_id}
                      </span>
                    )}
                    <h3 style={{ margin: 0 }}>{formatJobDisplayName(job)}</h3>
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: getJobTypeColor(job.job_type),
                        color: 'white',
                        fontSize: '12px'
                      }}
                    >
                      {JOB_TYPE_NAMES[job.job_type] || job.job_type}
                    </span>
                    {(job.notice_filename || (job.proposal_filenames && job.proposal_filenames.length > 0)) && (
                      <button
                        onClick={() => setExpandedFilesJobId(expandedFilesJobId === job.id ? null : job.id)}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          border: 'none',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span>📎 Файлы</span>
                        <span style={{ fontSize: '10px' }}>{expandedFilesJobId === job.id ? '▲' : '▼'}</span>
                      </button>
                    )}
                    <span>{t('jobs.created')}: {formatMoscowTime(job.created_at)}</span>
                    {job.status === 'completed' && job.completed_at && (
                      <>
                        <span style={{ color: '#4caf50' }}>
                          Завершено: {formatMoscowTime(job.completed_at)}
                        </span>
                        {job.started_at && (
                          <span style={{ color: '#2196f3' }}>
                            Длительность: {formatDuration(job.started_at, job.completed_at)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {expandedFilesJobId === job.id && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#fafafa',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      {job.notice_filename && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#666' }}>{getFileLabels(job.job_type).notice}:</strong>
                          <div style={{ marginLeft: '10px', color: '#333' }}>• {job.notice_filename}</div>
                        </div>
                      )}
                      {job.proposal_filenames && job.proposal_filenames.length > 0 && (
                        <div>
                          <strong style={{ color: '#666' }}>{getFileLabels(job.job_type).proposals}:</strong>
                          {job.proposal_filenames.map((filename, idx) => (
                            <div key={idx} style={{ marginLeft: '10px', color: '#333' }}>• {filename}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      padding: '5px 15px',
                      borderRadius: '20px',
                      backgroundColor: getStatusColor(job.status),
                      color: 'white',
                      fontSize: '14px',
                      height: 'fit-content'
                    }}
                  >
                    {t(`jobStatus.${job.status}`)}
                  </div>
                  {deletingJobId === job.id ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      <span style={{ color: '#666' }}>Удалить?</span>
                      <button
                        onClick={() => confirmDeleteJob(job.id)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: '#d32f2f',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Да
                      </button>
                      <button
                        onClick={() => setDeletingJobId(null)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: '#9e9e9e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Нет
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingJobId(job.id)}
                      title="Удалить задачу"
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: '#ffebee',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ffcdd2'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffebee'}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {job.status === 'processing' && (() => {
                const progress = calculateProgress(job);
                const remainingTime = formatRemainingTime(job);
                return (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{
                      width: '100%',
                      height: '20px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}>
                      <div
                        style={{
                          width: `${progress}%`,
                          height: '100%',
                          backgroundColor: '#2196f3',
                          transition: 'width 0.5s'
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '14px', marginTop: '5px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{progress}%</span>
                      {remainingTime && <span>{remainingTime}</span>}
                    </div>
                  </div>
                );
              })()}

              {job.status === 'completed' && (
                <button
                  onClick={() => downloadResult(job.id, job.job_name)}
                  style={{
                    marginTop: '10px',
                    padding: '10px 20px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {t('jobs.downloadResult')}
                </button>
              )}

              {job.status === 'error' && job.error_message && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#ffebee',
                  color: '#d32f2f',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  {t('jobs.error')}: {job.error_message}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          padding: '20px 0'
        }}>
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === 1 ? '#e0e0e0' : '#1976d2',
              color: currentPage === 1 ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            {'<<'}
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === 1 ? '#e0e0e0' : '#1976d2',
              color: currentPage === 1 ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            {'<'}
          </button>

          <span style={{ padding: '8px 16px', fontSize: '14px' }}>
            Страница {currentPage} из {totalPages} (всего: {totalJobs})
          </span>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#1976d2',
              color: currentPage === totalPages ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            {'>'}
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#1976d2',
              color: currentPage === totalPages ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            {'>>'}
          </button>
        </div>
      )}
    </div>
  );
};

export default JobsPage;
